import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  CheckIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";

function AdminDashboard() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Pagination & search state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTitle, setSearchTitle] = useState("");
  const [searchDesc, setSearchDesc] = useState("");
  const [searchTag, setSearchTag] = useState("");

  // Form state (shared between create and edit)
  const [editingJob, setEditingJob] = useState(null); // null = create mode, job object = edit mode
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [completionDate, setCompletionDate] = useState("");
  const [tags, setTags] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchJobs(1);
  }, []);

  const fetchJobs = async (page = 1) => {
    setLoading(true);
    try {
      let url = `/api/jobs?page=${page}`;
      if (searchTitle)
        url += `&search_title=${encodeURIComponent(searchTitle)}`;
      if (searchDesc) url += `&search_desc=${encodeURIComponent(searchDesc)}`;
      if (searchTag) url += `&tag=${encodeURIComponent(searchTag)}`;

      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch jobs");
      const data = await response.json();

      // Handle both old and new API response formats
      if (Array.isArray(data)) {
        setJobs(data);
        setTotalPages(1);
      } else {
        setJobs(data.jobs || []);
        setTotalPages(data.total_pages || 1);
      }
      setCurrentPage(page);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchJobs(1);
  };

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(""), 4000);
  };

  const startEdit = (job) => {
    setEditingJob(job);
    setTitle(job.title);
    setDescription(job.description);
    setTags((job.tags || []).join(", "));
    // Format date as YYYY-MM-DD for the input
    const d = new Date(job.completion_date);
    setCompletionDate(d.toISOString().split("T")[0]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingJob(null);
    setTitle("");
    setDescription("");
    setCompletionDate("");
    setTags("");
  };

  const handleSubmitJob = async (e) => {
    e.preventDefault();
    try {
      const tagsArray = tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t !== "");

      if (editingJob) {
        // Update existing job
        const response = await fetch(`/api/admin/jobs/${editingJob.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title,
            description,
            completion_date: completionDate,
            tags: tagsArray,
          }),
        });
        if (!response.ok) throw new Error("Failed to update job");
        showSuccess("Project updated successfully!");
        cancelEdit();
      } else {
        // Create new job
        const response = await fetch("/api/admin/jobs", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title,
            description,
            completion_date: completionDate,
            tags: tagsArray,
          }),
        });
        if (!response.ok) throw new Error("Failed to create job");
        setTitle("");
        setDescription("");
        setCompletionDate("");
        setTags("");
        showSuccess("Project created successfully!");
      }
      await fetchJobs(currentPage);
    } catch (err) {
      showError(err.message);
    }
  };

  const handleUploadImage = async (e, jobId) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("job_id", jobId);

    try {
      const response = await fetch("/api/admin/upload", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) throw new Error("Failed to upload image");
      showSuccess("Image uploaded successfully!");
      await fetchJobs(currentPage);
    } catch (err) {
      showError(err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteJob = async (id) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        const response = await fetch(`/api/admin/jobs/${id}`, {
          method: "DELETE",
        });
        if (!response.ok) throw new Error("Failed to delete job");
        showSuccess("Project deleted successfully!");
        await fetchJobs(currentPage);
      } catch (err) {
        showError(err.message);
      }
    }
  };

  return (
    <div className="pt-20 pb-20 min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 mt-2">
              Manage your projects and portfolio
            </p>
          </div>
          <Link
            to="/"
            className="text-blue-600 hover:text-blue-800 font-semibold transition"
          >
            ← Back to Home
          </Link>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg border border-red-300 animate-fade-in">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-100 text-green-700 rounded-lg border border-green-300 animate-fade-in flex items-center gap-2">
            <CheckIcon className="w-5 h-5 flex-shrink-0" />
            {successMessage}
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Add / Edit Job Form */}
          <div className="lg:col-span-1">
            <div
              className={`bg-white p-8 rounded-lg shadow-md sticky top-24 ${editingJob ? "border-2 border-blue-500" : ""}`}
            >
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  {editingJob ? (
                    <PencilIcon className="w-6 h-6 text-blue-600" />
                  ) : (
                    <PlusIcon className="w-6 h-6 text-blue-600" />
                  )}
                  <h2 className="text-2xl font-bold text-gray-800">
                    {editingJob ? "Edit Project" : "Add New Job"}
                  </h2>
                </div>
                {editingJob && (
                  <button
                    onClick={cancelEdit}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Cancel
                  </button>
                )}
              </div>
              {editingJob && (
                <p className="text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded mb-4">
                  Editing: <strong>{editingJob.title}</strong>
                </p>
              )}
              <form onSubmit={handleSubmitJob} className="space-y-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Project Title *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Kitchen Renovation"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Description *
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe the project in detail..."
                    required
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Completion Date *
                  </label>
                  <input
                    type="date"
                    value={completionDate}
                    onChange={(e) => setCompletionDate(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Tags (comma separated)
                  </label>
                  <input
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="e.g., kitchen, renovation, modern"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Add SEO-friendly tags to help with project discovery
                  </p>
                </div>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold py-3 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {editingJob ? (
                    <>
                      <CheckIcon className="w-5 h-5" />
                      Save Changes
                    </>
                  ) : (
                    <>
                      <PlusIcon className="w-5 h-5" />
                      Create Project
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Jobs List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              {/* Search/Filter Bar */}
              <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-cyan-50">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  Projects ({jobs.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <input
                    type="text"
                    placeholder="Search by title..."
                    value={searchTitle}
                    onChange={(e) => setSearchTitle(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Search by description..."
                    value={searchDesc}
                    onChange={(e) => setSearchDesc(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Filter by tag..."
                    value={searchTag}
                    onChange={(e) => setSearchTag(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <button
                    onClick={handleSearch}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2"
                  >
                    <MagnifyingGlassIcon className="w-4 h-4" />
                    Search
                  </button>
                </div>
              </div>

              <div className="divide-y divide-gray-200">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-gray-500">Loading projects...</p>
                  </div>
                ) : jobs.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <DocumentIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-lg font-semibold">No projects yet</p>
                    <p className="text-sm">
                      Create your first project using the form on the left
                    </p>
                  </div>
                ) : (
                  jobs.map((job) => (
                    <div
                      key={job.id}
                      className={`p-6 hover:bg-gray-50 transition-colors ${editingJob && editingJob.id === job.id ? "bg-blue-50 border-l-4 border-blue-500" : ""}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-grow">
                          <h3 className="font-bold text-lg text-gray-800 mb-1">
                            {job.title}
                          </h3>
                          <p className="text-gray-400 text-xs mb-2 font-mono">
                            /job/{job.slug}
                          </p>
                          <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                            {job.description}
                          </p>
                          <div className="flex items-center gap-1 text-gray-500 text-xs">
                            <CheckIcon className="w-4 h-4" />
                            <span>
                              Completed:{" "}
                              {new Date(
                                job.completion_date,
                              ).toLocaleDateString()}
                            </span>
                          </div>
                          {job.tags && job.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {job.tags.map((tag, idx) => (
                                <span
                                  key={idx}
                                  className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded"
                                >
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => startEdit(job)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg transition-colors text-sm flex items-center gap-1"
                          >
                            <PencilIcon className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteJob(job.id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-colors text-sm flex items-center gap-1"
                          >
                            <TrashIcon className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </div>

                      {/* Image Upload Section */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-2 mb-3">
                          <PhotoIcon className="w-5 h-5 text-gray-600" />
                          <p className="text-sm font-semibold text-gray-700">
                            Project Images
                          </p>
                        </div>

                        {job.images && job.images.length > 0 ? (
                          <div className="mb-3">
                            <div className="grid grid-cols-3 gap-2">
                              {job.images.map((image, idx) => (
                                <div
                                  key={idx}
                                  className="bg-gray-100 rounded-lg overflow-hidden"
                                >
                                  <img
                                    src={image.image_path}
                                    alt={image.image_description || "Project"}
                                    className="w-full h-20 object-cover"
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              {job.images.length} image(s) uploaded
                            </p>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 mb-3">
                            No images yet. Upload one below.
                          </p>
                        )}

                        <label className="flex items-center justify-center w-full px-3 py-2 bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors gap-2">
                          <PlusIcon className="w-4 h-4 text-blue-600" />
                          <span className="text-sm text-blue-600 font-semibold">
                            {uploadingImage ? "Uploading..." : "Add Image"}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleUploadImage(e, job.id)}
                            disabled={uploadingImage}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="px-8 py-6 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                  <button
                    onClick={() => fetchJobs(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded-lg transition-colors text-sm font-semibold"
                  >
                    ← Previous
                  </button>
                  <span className="text-gray-700 font-semibold">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => fetchJobs(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded-lg transition-colors text-sm font-semibold"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;
